// @flow

import * as React from 'react';
import _ from 'lodash';

import Pagination from '../Pagination';

const mergeQuery = (prev, { fetchMoreResult }) => {
  if (!fetchMoreResult) {
    return prev;
  }

  return fetchMoreResult;
};

const decodeCursor = (cursor?: string) => {
  if (!cursor) {
    return {};
  }

  try {
    const decodedCursor = global.atob(cursor);

    return JSON.parse(decodedCursor);
  } catch (e) {
    return {};
  }
};

export type PageInfo = {
  endCursor: string,
  hasNextPage: boolean,
  hasPreviousPage: boolean,
  startCursor: string,
};

export type Props = {
  /**
   * Provide Apollo's `fetchMore` function with the variables set to fetch
   * additional data. See https://www.apollographql.com/docs/react/features/pagination/#using-fetchmore
   * for additional documentation on how this works.
   */
  fetchMore: (options: {}) => void,
  /**
   * The total number of records available on server.
   */
  totalCount: number,
  /**
   * The pagination info object provided from the GraphQL pagination response.
   */
  pageInfo: PageInfo,
};

/**
 * Use this pagination to query Relay-style cursor pagination GraphQL queries.
 * It will automatically decode and encode cursor values to be sent back to the
 * GraphQL API.
 */
const GraphQLPagination = (props: Props) => {
  const { fetchMore, totalCount, pageInfo } = props;
  const { hasPreviousPage, hasNextPage, endCursor, startCursor } = pageInfo;

  const parsedEndCursor = decodeCursor(endCursor);
  const parsedStartCursor = decodeCursor(startCursor);
  const endIndex = _.get(parsedEndCursor, ['offset'], 0);
  const startIndex = _.get(parsedStartCursor, ['offset'], 0);

  const onNextPage = () => {
    if (hasNextPage) {
      fetchMore({
        variables: {
          after: endCursor,
        },
        updateQuery: mergeQuery,
      });
    }
  };

  const onPrevPage = () => {
    if (hasPreviousPage) {
      fetchMore({
        variables: {
          before: startCursor,
        },
        updateQuery: mergeQuery,
      });
    }
  };

  return (
    <Pagination
      canDecrement={hasPreviousPage}
      canIncrement={hasNextPage}
      endIndex={endIndex}
      onNextPage={onNextPage}
      onPrevPage={onPrevPage}
      startIndex={startIndex}
      totalCount={totalCount}
    />
  );
};

export default GraphQLPagination;
